import { RequestOptions, RequestResponse } from '../core/types';

/**
 * HTTP 请求工具 - 使用浏览器原生 fetch API
 */
export class Request {
  /** 默认超时时间 */
  private static readonly DEFAULT_TIMEOUT = 10000;

  /**
   * 发起 HTTP 请求
   */
  static async fetch<T = any>(url: string, options: RequestOptions = {}): Promise<RequestResponse<T>> {
    const { method = 'GET', headers = {}, body, timeout = this.DEFAULT_TIMEOUT } = options;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data: T = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : (await response.text()) as T;

      return { data, status: response.status, statusText: response.statusText };
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * GET 请求
   */
  static get<T = any>(url: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<RequestResponse<T>> {
    return this.fetch<T>(url, { ...options, method: 'GET' });
  }

  /**
   * POST 请求（JSON 格式）
   */
  static post<T = any>(url: string, body: any, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<RequestResponse<T>> {
    return this.fetch<T>(url, {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * POST 请求（form-urlencoded 格式，避免跨域预检）
   */
  static postForm<T = any>(url: string, data: Record<string, string | undefined>, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<RequestResponse<T>> {
    const formData = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
      if (value != null) formData.append(key, value);
    }

    return this.fetch<T>(url, {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...options.headers },
      body: formData.toString(),
    });
  }
}
